/**
 * Responsibility: tell one printing of a card from another, by its name.
 *
 * **A printing is not a detail, it is most of what a copy is worth.** A
 * reverse holo, a first edition and a plain copy of the same card share an
 * illustration, a collector number and a name, and their prices differ by
 * multiples. A collection that shows all three as "Medicham" cannot be
 * priced, and somebody looking for the one they own cannot find it.
 *
 * The catalogue already carries the distinction, in brackets, in the middle
 * of the name: `Medicham [Reverse Holo] #81`. This pulls it out so it can be
 * shown as what it is rather than read as punctuation.
 *
 * **The same vocabulary as the scanner's `printings.py`**, deliberately. The
 * two answer the same question - which of these names are the same picture -
 * for different reasons: the scanner groups them because no photograph can
 * separate them, and this separates them because a wallet can. If the lists
 * drift, a card will group in the shortlist and split in the collection, and
 * the person will be told they own something they cannot find.
 */

/**
 * What a catalogue writes in brackets when it means "the same card, printed
 * differently". Anything else in brackets - `[Anime]`, `[Extended Art]` - is
 * a different illustration and belongs to the card's name.
 */
const SAME_ART = [
  'reverse holo',
  'mirror reverse holo',
  'mirror holo',
  'cosmos holo',
  'holo',
  'reverse',
  '1st edition',
  'first edition',
  'unlimited',
  'shadowless',
  'stamped',
  'staff',
  'prerelease',
  'prize pack',
  'pokemon center',
  'league',
];

const BRACKET = /[[(]([^\])]*)[\])]/g;

const isPrinting = (inside: string) =>
  SAME_ART.some((form) => inside.toLowerCase().includes(form));

/** How this copy was printed, or empty when it is the plain one. */
export function printingOf(name: string): string {
  const found: string[] = [];
  for (const match of name.matchAll(BRACKET)) {
    if (isPrinting(match[1])) found.push(match[1].trim());
  }
  return found.join(', ');
}

/** The card's name without the printing, for showing them side by side. */
export function baseName(name: string): string {
  return name.replace(BRACKET, (whole, inside: string) => (isPrinting(inside) ? '' : whole))
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * A short label for the printing, for somewhere there is no room.
 *
 * Shortened by hand rather than truncated: `Rev Holo` reads, `Reverse H…`
 * does not, and these are the handful that actually occur.
 */
const SHORT: Record<string, string> = {
  'reverse holo': 'Rev Holo',
  'mirror reverse holo': 'Mirror',
  'mirror holo': 'Mirror',
  'cosmos holo': 'Cosmos',
  holo: 'Holo',
  reverse: 'Rev',
  '1st edition': '1st Ed',
  'first edition': '1st Ed',
  unlimited: 'Unltd',
  shadowless: 'Shadowless',
  stamped: 'Stamped',
  staff: 'Staff',
  prerelease: 'Prerel',
  'prize pack': 'Prize',
  'pokemon center': 'PC',
  league: 'League',
};

export function shortPrinting(name: string): string {
  const printing = printingOf(name).toLowerCase();
  if (!printing) return '';
  for (const [form, label] of Object.entries(SHORT)) {
    if (printing.includes(form)) return label;
  }
  return printingOf(name);
}
